import type { PublicAudioSource } from "@hitster/shared";
import { createAudioProvider, type AudioProvider } from "../providers/AudioProvider";
import { isEmbeddedStreamSource } from "../providers/EmbeddedStreamProvider";

const VOLUME_KEY = "hitster_volume";
/** WAV silencieux minimal pour débloquer l'autoplay dans le geste utilisateur courant. */
const SILENT_WAV =
  "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAZGF0YQQAAAAAAA==";

const storedVolume = localStorage.getItem(VOLUME_KEY);
let volume = storedVolume !== null ? Number(storedVolume) : 0.1;
if (Number.isNaN(volume)) volume = 0.1;
volume = Math.max(0, Math.min(1, volume));

let unlocked = false;
let currentProvider: AudioProvider | null = null;
let playGeneration = 0;
const listeners = new Set<() => void>();

function notify() {
  listeners.forEach((fn) => fn());
}

function applyVolume(provider: AudioProvider, level: number): void {
  provider.setVolume(level);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function withRetryParam(url: string, attempt: number): string {
  if (attempt <= 1) return url;
  const base = url.split("?")[0]!;
  return `${base}?retry=${attempt}&t=${Date.now()}`;
}

async function attemptPlay(
  source: PublicAudioSource,
  scheduledAt: number,
  generation: number,
): Promise<boolean> {
  const isStream = isEmbeddedStreamSource(source);
  const delay = Math.max(0, scheduledAt - Date.now());

  currentProvider?.stop();
  const provider = createAudioProvider(source);
  currentProvider = provider;
  applyVolume(provider, volume);

  const loadPromise = provider.load(source);
  if (isStream) {
    await loadPromise;
    if (delay > 0) await sleep(delay);
  } else if (delay > 0) {
    await Promise.all([loadPromise, sleep(delay)]);
  } else {
    await loadPromise;
  }

  if (generation !== playGeneration || currentProvider !== provider) {
    provider.stop();
    return false;
  }

  applyVolume(provider, volume);

  if (volume === 0) {
    provider.stop();
    if (currentProvider === provider) currentProvider = null;
    unlocked = true;
    notify();
    return true;
  }

  await provider.play();
  if (generation !== playGeneration || currentProvider !== provider) {
    provider.stop();
    return false;
  }

  unlocked = true;
  notify();
  return true;
}

export const audioManager = {
  getVolume(): number {
    return volume;
  },

  setVolume(value: number): void {
    volume = Math.max(0, Math.min(1, value));
    localStorage.setItem(VOLUME_KEY, String(volume));
    if (currentProvider) applyVolume(currentProvider, volume);
    notify();
    if (volume > 0) void this.unlockWithUserGesture();
  },

  isUnlocked(): boolean {
    return unlocked;
  },

  /** @deprecated Préférer unlockWithUserGesture pendant un geste utilisateur. */
  unlock(): void {
    unlocked = true;
    notify();
  },

  /** À appeler pendant un clic/tap/clavier — seul moyen fiable côté navigateur. */
  async unlockWithUserGesture(): Promise<boolean> {
    if (unlocked) return true;
    if (volume === 0) {
      unlocked = true;
      notify();
      return true;
    }
    try {
      const probe = new Audio(SILENT_WAV);
      probe.volume = Math.min(volume, 0.001);
      probe.muted = volume === 0;
      await probe.play();
      probe.pause();
      probe.src = "";
      unlocked = true;
      notify();
      return true;
    } catch {
      return false;
    }
  },

  subscribe(fn: () => void): () => void {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },

  stop(): void {
    playGeneration += 1;
    currentProvider?.stop();
    currentProvider = null;
  },

  async play(source: PublicAudioSource, scheduledAt: number, maxAttempts = 3): Promise<boolean> {
    const generation = ++playGeneration;

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      const attemptSource =
        attempt > 1 ? { ...source, url: withRetryParam(source.url, attempt) } : source;
      const attemptSchedule = attempt > 1 ? Date.now() + 200 : scheduledAt;

      try {
        const ok = await attemptPlay(attemptSource, attemptSchedule, generation);
        if (ok) return true;
        if (generation !== playGeneration) return false;
      } catch {
        if (generation !== playGeneration) return false;
      }

      if (attempt < maxAttempts) {
        await sleep(800 * attempt);
      }
    }

    if (generation === playGeneration) currentProvider = null;
    return false;
  },
};
